import React from "react";
import { Upload, Undo2 } from "lucide-react";

/*
 * Standalone hero-video uploader slot for Admin → Branding.
 * Props:
 *   slot:     "upload" | "analyze" | "estimate"
 *   label:    human-readable string
 *   url:      current branding URL (empty string = bundled default)
 *   busyPct:  0..100 upload progress (undefined = idle)
 *   onUpload: (file: File) => void
 *   onReset:  () => void
 *
 * Invariants (port kit §6): muted + autoPlay + loop + playsInline +
 * preload="metadata" (iOS autoplay), key={previewSrc} so a reset tears
 * down the old video, 16:9 + object-cover, progress inline on the button.
 */
export function VideoSlotUploader({ slot, label, url, busyPct, onUpload, onReset }) {
  const previewSrc = url
    ? url.startsWith("http")
      ? url
      : `${process.env.REACT_APP_BACKEND_URL}${url}`
    : "/uploading.mp4";
  const isUploading = typeof busyPct === "number" && busyPct > 0 && busyPct < 100;
  return (
    <div
      className="overflow-hidden rounded-md border border-white/15 bg-white/[0.03]"
      data-testid={`admin-branding-video-slot-${slot}`}
    >
      <div className="aspect-video bg-black">
        <video
          key={previewSrc}
          src={previewSrc}
          className="h-full w-full object-cover"
          muted
          autoPlay
          loop
          playsInline
          preload="metadata"
          data-testid={`admin-branding-video-preview-${slot}`}
        />
      </div>
      <div className="space-y-2 p-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="truncate text-[11px] font-bold uppercase tracking-wider text-white/70">
            {label}
          </div>
          <span
            className={`rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
              url
                ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                : "border-white/15 text-white/40"
            }`}
            data-testid={`admin-branding-video-badge-${slot}`}
          >
            {url ? "custom" : "default"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label
            className={`inline-flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-bold transition-colors ${
              isUploading
                ? "cursor-wait border-white/15 bg-white/5 text-white/40"
                : "border-white/15 text-white/80 hover:bg-white/10"
            }`}
          >
            <Upload className="h-3 w-3" />
            {isUploading ? `Uploading ${busyPct}%` : "Upload"}
            <input
              type="file"
              accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) onUpload(f);
              }}
              data-testid={`admin-branding-video-input-${slot}`}
              disabled={isUploading}
            />
          </label>
          {url && !isUploading && (
            <button
              type="button"
              onClick={onReset}
              data-testid={`admin-branding-video-reset-${slot}`}
              className="inline-flex items-center gap-1 rounded-md border border-white/15 px-2 py-1 text-[11px] font-bold text-white/70 hover:bg-white/10"
            >
              <Undo2 className="h-3 w-3" />
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default VideoSlotUploader;
